import React, { useEffect, useRef } from "react";
import { assetUrl } from "../adminApi";
import { useI18n } from "../i18n";
import { useSiteData } from "../siteData";

/**
 * Client / logo band. Auto-scrolls and can be dragged left-right with mouse or touch.
 * Rendered only where the admin panel placed it (page + anchor).
 */
export default function LogoSlider({ page = "home", anchor = "bottom" }) {
  const { t, loc } = useI18n();
  const { content } = useSiteData();
  const clients = content.clients || {};
  const legacy = content.slider || {};
  const cfg = {
    title: clients.sliderTitle || legacy.title,
    speed: clients.sliderSpeed || legacy.speed,
    placements: (clients.placements && clients.placements.length ? clients.placements : legacy.placements) || [],
  };
  const items = clients.items || [];
  const trackRef = useRef(null);
  const state = useRef({ offset: 0, dragging: false, startX: 0, startOffset: 0, half: 0 });

  const placements = Array.isArray(cfg.placements) ? cfg.placements : [];
  const placed = placements.some(
    (p) => p && p.visible !== false && p.page === page && (p.anchor || "bottom") === anchor
  );
  const speed = Number(cfg.speed) > 0 ? Number(cfg.speed) : 34;

  useEffect(() => {
    if (!placed || !items.length) return undefined;
    const track = trackRef.current;
    if (!track) return undefined;
    let raf = 0;
    let last = performance.now();

    const measure = () => {
      state.current.half = track.scrollWidth / 2 || 1;
    };
    measure();

    const step = (now) => {
      const dt = Math.min(now - last, 60);
      last = now;
      const s = state.current;
      if (!s.dragging) s.offset -= (speed * dt) / 1000;
      if (s.half) {
        if (s.offset <= -s.half) s.offset += s.half;
        if (s.offset > 0) s.offset -= s.half;
      }
      track.style.transform = `translate3d(${s.offset}px,0,0)`;
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", measure);
    };
  }, [placed, items.length, speed]);

  if (!placed || !items.length) return null;

  const row = [...items, ...items];

  const onDown = (e) => {
    const s = state.current;
    s.dragging = true;
    s.startX = e.clientX;
    s.startOffset = s.offset;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onMove = (e) => {
    const s = state.current;
    if (!s.dragging) return;
    s.offset = s.startOffset + (e.clientX - s.startX);
  };
  const onUp = () => {
    state.current.dragging = false;
  };

  return (
    <section
      className="bg-[#F2F0EB] border-t border-b border-stone-200 py-7 overflow-hidden select-none"
      data-testid={`logo-slider-${page}-${anchor}`}
    >
      <div className="shell mb-5 flex items-baseline justify-between gap-4">
        <p className="mono text-[9.5px] text-[#6B675F]">{loc(cfg.title) || t("clients.label")}</p>
        <span className="mono text-[8px] text-[#A8A29A] hidden sm:block">DRAG</span>
      </div>
      <div
        className="overflow-hidden cursor-grab active:cursor-grabbing"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        onPointerLeave={onUp}
        style={{ touchAction: "pan-y" }}
      >
        <div ref={trackRef} className="flex items-center w-max will-change-transform">
          {row.map((c, i) => (
            <div key={`${c.name}-${i}`} className="flex items-center shrink-0">
              {c.logo ? (
                <img
                  src={assetUrl(c.logo)}
                  alt={c.name}
                  draggable="false"
                  className="h-[34px] lg:h-[42px] w-auto mx-8 object-contain opacity-70 hover:opacity-100 transition-opacity duration-300"
                />
              ) : (
                <span className="display text-[24px] sm:text-[30px] lg:text-[34px] text-[#141414]/80 px-8 whitespace-nowrap">
                  {c.name}
                </span>
              )}
              <span className="w-1.5 h-1.5 rounded-full bg-[#C8B29B] shrink-0" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
